"use client";

import { useEffect } from "react";

import { ButtonLink } from "@/components/ui/button-link";
import { ErrorState } from "@/components/ui/states";

/**
 * Catches render errors anywhere below the root layout.
 *
 * The message is deliberately generic — a thrown error can carry a database
 * string or a stack, and this component renders in the browser. `digest` is
 * shown because it is the only handle a user can quote in a support request
 * that maps to the real error in the server logs.
 */
export default function AppError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error("[app] unhandled error", error);
  }, [error]);

  return (
    <main className="mx-auto flex w-full max-w-2xl flex-1 flex-col items-center justify-center gap-6 px-6 py-24">
      <ErrorState
        title="Something went wrong"
        description="This page failed to load. The problem has been logged."
        onRetry={reset}
      />

      {error.digest ? (
        <p className="text-muted font-mono text-xs">Reference: {error.digest}</p>
      ) : null}

      <ButtonLink href="/" variant="secondary">
        Back to home
      </ButtonLink>
    </main>
  );
}
